/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilePdfOutlined, MoreOutlined } from '@ant-design/icons'
import {
  INewTumRegister,
  IParamsGetNewTumRegister,
} from 'app/api/newTumRegister/model'

import { SorterResult } from 'antd/es/table/interface'
import R from 'app/assets/R'
import { PermissionEnum } from 'app/common/enum'
import { ISort } from 'app/common/interface'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCheckbox } from 'app/components/common/BaseCheckbox'
import { BaseCol } from 'app/components/common/BaseCol'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { TRADE_UNION_MEMBER_DETAILS_PATH } from 'app/components/router/route-path'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce, usePagination } from 'app/hook'
import { useGetNewTumRegister } from 'app/react-query/hook/newTumRegister'
import { IResponseQueryList } from 'app/react-query/model/common'
import { handleDownloadFile } from 'app/service/general/service'
import { isEqual } from 'lodash'
import { Pagination, initialPagination } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ApproveTumModal, RejectTumModal } from './components'
import { ReApproveTumModal } from './components/ReApproveTumModal'
import { ENewTumRegisterStatus, IDataTable } from './type'

interface IProps {
  status: string
}

export const useHook = ({ status }: IProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [filter, setFilter] = useState<IParamsGetNewTumRegister>({})
  const [tumSelected, setTumSelected] = useState<INewTumRegister[]>([])
  const [sorts, setSort] = useState<ISort[]>([])

  const filterDebounce = useDebounce(filter, 500)

  const { flattenDataList } = usePagination()
  const { data: dataApi, isLoading } = useGetNewTumRegister({
    search: filterDebounce?.search,
    page: pagination?.current,
    limit: pagination?.pageSize,
    status: status,
    sorts,
  })

  const flattenData = useMemo(() => {
    const res: IResponseQueryList<INewTumRegister[]> = flattenDataList(dataApi)

    return res
  }, [dataApi])

  const data: IDataTable[] = useMemo(() => {
    return (
      flattenData?.data?.map((item, index) => ({
        ...item,
        key: index + 1,
      })) ?? []
    )
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? data?.length
  }, [flattenData, data])

  const onChangeFilter = (newFilter: IParamsGetNewTumRegister) => {
    setFilter(newFilter)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const { user, verifyPermissionAndRedirect, verifyPermission} = useVerifyPermission()

  useEffect(() => {
    verifyPermissionAndRedirect(PermissionEnum.ReadNewTumRegister)
  }, [user])

  const onClickDownloadFile = async (file?: string) => {
    if (file) {
      await handleDownloadFile({
        fileUrl: file,
      })
    }
  }

  const onChecked = (record: INewTumRegister, checked: boolean) => {
    let newTums = tumSelected?.filter(itm => !isEqual(itm?.id, record?.id))

    if (!checked) {
      newTums = newTums?.concat(record)
    }

    setTumSelected(newTums)
  }

  const onSelectAll = () => {
    if (tumSelected?.length) {
      setTumSelected([])
    } else {
      setTumSelected(data)
    }
  }

  const columns = useMemo(() => {
    let options: any[] = []

    if (status === ENewTumRegisterStatus.WAITING) {
      options = options.concat([
        convertColumnTable<any>({
          key: 'checkbox',
          title: (
            <BaseCheckbox
              checked={
                !!data?.length && isEqual(tumSelected?.length, data?.length)
              }
              onChange={onSelectAll}
              disabled={!verifyPermission(PermissionEnum.ApproveNewTumRegister)}
            />
          ),
          fixed: 'left',
          classNameWidthColumnOverwrite: 'number-column',
          render: (_, record: INewTumRegister) => {
            const checked = !!tumSelected?.find(itm =>
              isEqual(itm?.id, record?.id),
            )
            return (
              <BaseCheckbox
                checked={checked}
                onChange={() => onChecked(record, checked)}
                disabled={!verifyPermission(PermissionEnum.ApproveNewTumRegister)}
              />
            )
          },
        }),
      ])
    }

    options = options.concat([
      convertColumnTable<INewTumRegister>({
        key: 'fullName',
        title: t(R.strings.full_name),
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <RenderValueTable
              type="Link"
              value={record?.fullName}
              onClick={() =>
                navigate(TRADE_UNION_MEMBER_DETAILS_PATH, {
                  state: {
                    tum: record,
                  },
                })
              }
            />
          )
        },
      }),
      convertColumnTable<INewTumRegister>({
        key: 'mql',
        sorter: true,
        title: t(R.strings.id_management),
        render: (_, record) => {
          return (
            <BaseText fontWeight="semibold" children={record?.mql ?? '-'} />
          )
        },
      }),
      convertColumnTable<INewTumRegister>({
        key: 'email',
        title: t(R.strings.email),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.email ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<INewTumRegister>({
        key: 'tenDonVi',
        title: 'Đơn vị UEH',
        sorter: true,
        render: text => {
          return <BaseText fontWeight="medium" opacity="0.7" children={text} />
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'gioiTinh',
        title: t(R.strings.gender),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.gioiTinh ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'activityEngagement',
        title: t(R.strings.status_union_activities),
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.activityEngagement?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'school',
        title: 'Công đoàn bộ phận',
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.school?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'created_by',
        title: 'Người tạo',
        render: (_, record) => {
          return (
            <BaseSpace size={4}>
              <BaseText
                fontWeight="semibold"
                children={record?.created_by?.full_name ?? '-'}
              />
              <BaseText
                fontWeight="medium"
                opacity="0.7"
                children={record?.created_by?.email ?? '-'}
              />
            </BaseSpace>
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'dienThoaiDiDong',
        title: t(R.strings.phone),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.dienThoaiDiDong ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'file',
        title: 'Đơn gia nhập',
        render: (_, record) => {
          return (
            <BaseRow
              gutter={6}
              align={'middle'}
              style={{ cursor: 'pointer' }}
              onClick={() => onClickDownloadFile(record?.file)}
              justify={'start'}
            >
              <BaseCol>
                <FilePdfOutlined rev={undefined} />
              </BaseCol>
              <BaseCol>
                <BaseText
                  children={record?.file_name ?? 'Không có'}
                  colorText="collapseBackgroundColor"
                  fontWeight="medium"
                />
              </BaseCol>
            </BaseRow>
          )
        },
      }),
    ])

    if (status === ENewTumRegisterStatus.WAITING && verifyPermission(PermissionEnum.ApproveNewTumRegister)) {
      options = options?.concat([
        convertColumnTable<INewTumRegister>({
          key: 'id',
          width: '5%',
          classNameWidthColumnOverwrite: 'number-column',
          fixed: 'right',
          render: (_, record) => {
            return (
              <>
                <BasePopover
                  trigger={'click'}
                  content={
                    <>
                      <BaseSpace>
                        <ApproveTumModal
                          name={record.fullName}
                          id={record.id}
                          file={record?.file}
                          fileName={record?.file_name}
                        />

                        <RejectTumModal
                          name={record.fullName}
                          id={record.id}
                          file={record?.file}
                          fileName={record?.file_name}
                        />
                      </BaseSpace>
                    </>
                  }
                >
                  <BaseButton
                    icon={<MoreOutlined rev={undefined} />}
                    type="text"
                  />
                </BasePopover>
              </>
            )
          },
        }),
      ])
    }

    if (status === ENewTumRegisterStatus.REJECTED && verifyPermission(PermissionEnum.ApproveNewTumRegister)) {
      options = options.concat([
        convertColumnTable<INewTumRegister>({
          key: 'reason',
          fixed: 'right',
          title: "Lý do từ chối",
          render: (_, record) => {
            return (
              <BaseText children={record?.reason} colorText="statesRedColor" />
            )
          },
        }),
        convertColumnTable<INewTumRegister>({
          key: 'id',
          fixed: 'right',
          render: (_, record) => {
            return (
              <ReApproveTumModal
                name={record.fullName}
                id={record.id}
                file={record?.file}
                fileName={record?.file_name}
              />
            )
          },
        }),
      ])
    }

    return options
  }, [t, tumSelected, data, status])

  const handleTableChange = (
    pagination: Pagination,
    _,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  return {
    columns,
    pagination: {
      ...pagination,
      total,
    },
    onChangeFilter,
    filter,
    isLoading,
    total,
    handleChangePagination: handleTableChange,
    data,
    tumSelected,
  }
}
